import { create } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import type { WorkspaceMode } from '@auw/workspace-utils';
import { isMobileViewport } from '@auw/workspace-utils';
import { activeTenantNamespacedKey as tenantNamespacedKey } from '@auw/tenant';

type RememberedIdByMode = Partial<Record<WorkspaceMode, string | null>>;
type PageSidebarOpenByMode = Partial<Record<WorkspaceMode, boolean>>;

const DEFAULT_PAGE_SIDEBAR_OPEN_BY_MODE: Partial<Record<WorkspaceMode, boolean>> = {
  // 통합 에이전트 / 라이브 에이전트도 페이지 내부 좌측 목록 패널을 기본 펼침으로 둔다
  // (대화 목록이 바로 보이도록). 사용자가 접으면 그 선택은 localStorage 에 저장돼 유지된다.
  agent: true,
  'live-agent': true,
  document: true,
  storage: true,
  message: true,
};

function getDefaultPageSidebarOpen(mode: WorkspaceMode): boolean {
  // 모바일(<=768px)에서는 페이지 좌측 목록 패널이 전체 오버레이로 화면을
  // 덮으므로 기본 접힘으로 둔다. 사용자가 명시적으로 토글해 저장한 값은
  // pageSidebarOpenByMode 에 남아 그대로 존중된다(이 함수는 미저장일 때만 사용).
  if (isMobileViewport()) return false;
  return DEFAULT_PAGE_SIDEBAR_OPEN_BY_MODE[mode] ?? false;
}

interface WorkspaceNavigationState {
  rememberedIdByMode: RememberedIdByMode;
  pageSidebarOpenByMode: PageSidebarOpenByMode;
  rememberModeSelection: (mode: WorkspaceMode, conversationId: string | null) => void;
  clearRememberedModeSelection: (mode: WorkspaceMode) => void;
  isPageSidebarOpen: (mode: WorkspaceMode) => boolean;
  setPageSidebarOpen: (mode: WorkspaceMode, open: boolean) => void;
  togglePageSidebar: (mode: WorkspaceMode) => void;
  resetWorkspaceNavigation: () => void;
}

export const useWorkspaceNavigationStore = create<WorkspaceNavigationState>()(
  persist(
    (set, get) => ({
      rememberedIdByMode: {},
      pageSidebarOpenByMode: {},
      rememberModeSelection: (mode, conversationId) => {
        set((state) => ({
          rememberedIdByMode: {
            ...state.rememberedIdByMode,
            [mode]: conversationId,
          },
        }));
      },
      clearRememberedModeSelection: (mode) => {
        set((state) => ({
          rememberedIdByMode: {
            ...state.rememberedIdByMode,
            [mode]: null,
          },
        }));
      },
      isPageSidebarOpen: (mode) => get().pageSidebarOpenByMode[mode] ?? getDefaultPageSidebarOpen(mode),
      setPageSidebarOpen: (mode, open) => {
        set((state) => ({
          pageSidebarOpenByMode: {
            ...state.pageSidebarOpenByMode,
            [mode]: open,
          },
        }));
      },
      togglePageSidebar: (mode) => {
        set((state) => ({
          pageSidebarOpenByMode: {
            ...state.pageSidebarOpenByMode,
            [mode]: !(state.pageSidebarOpenByMode[mode] ?? getDefaultPageSidebarOpen(mode)),
          },
        }));
      },
      resetWorkspaceNavigation: () => {
        set({
          rememberedIdByMode: {},
          pageSidebarOpenByMode: {},
        });
      },
    }),
    {
      name: tenantNamespacedKey('workspace-navigation'),
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        pageSidebarOpenByMode: state.pageSidebarOpenByMode,
      }),
    },
  ),
);
